import { Dispatch, SetStateAction } from 'react';

import { PaymentMethod, CloverPayment, PaymentMethodType } from 'types';

/**
 * Calculate the total amount of all payment methods including processing fees if enabled.
 * @param {PaymentMethod[]} paymentMethods - An array of payment methods.
 * @param {boolean} [isProcessingFeeEnabled=false] - Whether to include processing fees in calculations.
 * @returns {number} The total amount of all payment methods.
 */
export const getPaymentMethodsWithProcessingFee = (
  paymentMethods: PaymentMethod[],
  isProcessingFeeEnabled?: boolean
) =>
  paymentMethods?.reduce((acc, item) => {
    let amountWithFee = item.amount;
    if (item.type === PaymentMethodType.Credit && isProcessingFeeEnabled) {
      const processingFee = item.proccessingFee || 0;
      amountWithFee += processingFee;
    }
    return acc + amountWithFee;
  }, 0);

const appendSingleCloverPayment = <
  T extends { tip?: number; paymentMethods: PaymentMethod[] },
>({
  prev,
  payment,
  companyProcessingFees,
  isProcessingFeeEnabled,
}: {
  prev: T;
  payment: CloverPayment;
  companyProcessingFees?: number;
  isProcessingFeeEnabled?: boolean;
}): T => {
  const isDuplicateById = prev.paymentMethods.some(
    (method) => method.cloverPaymentId === payment.paymentId
  );

  if (isDuplicateById) {
    console.warn(
      '[Checkout] Skipping duplicate payment by ID:',
      payment.paymentId
    );
    return prev;
  }

  const amount = Number((payment.amount / 100).toFixed(2));
  const tip = payment?.tipAmount
    ? Number((payment.tipAmount / 100).toFixed(2))
    : undefined;

  const processingFee = payment?.processingFee
    ? Number((payment.processingFee / 100).toFixed(2))
    : Number(
        ((amount - (tip ?? 0)) * ((companyProcessingFees ?? 0) / 100)).toFixed(
          2
        )
      );

  const currentTipAmount = (prev.tip ?? 0) + (tip ?? 0);

  return {
    ...prev,
    tip: currentTipAmount || undefined,
    paymentMethods: [
      ...prev.paymentMethods,
      {
        totalAmount: amount,
        last4CC: payment?.last4,
        amount: amount - processingFee,
        type: PaymentMethodType.Credit,
        cloverPaymentId: payment.paymentId,
        id: `${amount}-${PaymentMethodType.Credit}`,
        proccessingFee: isProcessingFeeEnabled ? processingFee : 0,
      },
    ],
  };
};

/**
 * Appends a Clover payment to the form state.
 * @param {CloverPayment} payment - The payment received from Clover.
 * @param {Dispatch<SetStateAction<T>>} setFormValues - The function to update the form state.
 * @param {number} [companyProcessingFees] - The company's processing fees percentage.
 * @param {boolean} [isProcessingFeeEnabled=false] - Whether to include processing fees in calculations.
 */
export const appendCloverPaymentToForm = <
  T extends { tip?: number; paymentMethods: PaymentMethod[] },
>({
  payment,
  setFormValues,
  companyProcessingFees,
  isProcessingFeeEnabled,
}: {
  payment: CloverPayment;
  companyProcessingFees?: number;
  isProcessingFeeEnabled?: boolean;
  setFormValues: Dispatch<SetStateAction<T>>;
}) => {
  console.log('[Checkout] Clover payment received:', payment);

  setFormValues((prev) =>
    appendSingleCloverPayment({
      prev,
      payment,
      companyProcessingFees,
      isProcessingFeeEnabled,
    })
  );
};

export const appendCloverPaymentsToForm = <
  T extends { tip?: number; paymentMethods: PaymentMethod[] },
>({
  payments,
  setFormValues,
  companyProcessingFees,
  isProcessingFeeEnabled,
}: {
  payments: CloverPayment[];
  companyProcessingFees?: number;
  isProcessingFeeEnabled?: boolean;
  setFormValues: Dispatch<SetStateAction<T>>;
}) =>
  setFormValues((prev) =>
    payments.reduce(
      (acc, payment) =>
        appendSingleCloverPayment({
          payment,
          prev: acc,
          companyProcessingFees,
          isProcessingFeeEnabled,
        }),
      prev
    )
  );
